import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button, Container, Table, Row, Col, Form, Modal, Spinner } from "react-bootstrap";
import ReportsService from "../../../../../../../../../services/reports-service";

const ReportReview = ({ formData, nextStep }) => {
    const [showMissingModal, setShowMissingModal] = useState(false);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [missingFields, setMissingFields] = useState([]);
    const [loading, setLoading] = useState(false);
    const reportsService = new ReportsService();
    const { applicationId, isin, interestPaymentDate } = useParams();
    const navigate = useNavigate();

    const validateFields = () => {
        let missing = [];
        if (!formData.clientName) missing.push("Client's Name");
        if (!formData.bpId) missing.push("bpId");
        if (!formData.esipReferenceNumber) missing.push("e-SIP Reference Number");
        if (!formData.amount) missing.push("Amount");
        if (!formData.documents.pdf1) missing.push("PDF 1");
        if (!formData.documents.pdf2) missing.push("PDF 2");

        return missing;
    };

    const handleConfirm = () => {
        const missing = validateFields();
        if (missing.length > 0) {
            setMissingFields(missing);
            setShowMissingModal(true);
            return;
        }
        // All required fields are present, show the confirmation modal.
        setShowConfirmModal(true);
    };

    const handleContinue = () => {
        setLoading(true);
        // Close the confirmation modal while processing
        setShowConfirmModal(false);
        const submission = true;
        reportsService
            .patchTaxExemptApplication(applicationId, isin, interestPaymentDate, formData, submission)
            .then(() => {
                setLoading(false);
                setShowSuccessModal(true);
                // Reload the page after 3 seconds.
                setTimeout(() => {
                    navigate(`/tax-exempt-reports`)
                }, 3000);
            })
            .catch((error) => {
                setLoading(false);
                console.error("Error submitting data:", error);
                alert("There was an error submitting the data.");
            });
    };

    return (
        <>
            <Container className="report-container mb-3">
                <h2>Security Details Preview</h2>
                <Row className="mb-4 px-5">
                    <Col>
                        <Row className="d-flex justify-content-between mb-3">
                            <Col className="col-6">
                                <Form.Group>
                                    <Form.Label>Client's Name</Form.Label>
                                    <Form.Control type="text" value={formData.clientName} readOnly />
                                </Form.Group>
                            </Col>
                            <Col className="col-4">
                                <Form.Group>
                                    <Form.Label>bpId</Form.Label>
                                    <Form.Control type="text" value={formData.bpId} readOnly />
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row className="d-flex justify-content-between mb-3">
                            <Col className="col-4">
                                <Form.Group>
                                    <Form.Label>e-SIP Reference Number</Form.Label>
                                    <Form.Control type="text" value={formData.esipReferenceNumber} readOnly />
                                </Form.Group>
                            </Col>

                            <Col className="col-4">
                                <Form.Group>
                                    <Form.Label>Amount</Form.Label>
                                    <Form.Control type="text" value={formData.amount} readOnly />
                                </Form.Group>
                            </Col>
                        </Row>
                    </Col>
                </Row>
            </Container>

            <Container className="report-container mb-3">
                <h3>Rejection History</h3>
                <Table striped bordered>
                    <thead>
                        <tr>
                            <th>Rejection</th>
                            <th>By</th>
                            <th>Remark</th>
                            <th>Date of Rejection</th>
                        </tr>
                    </thead>
                    <tbody>
                        {formData.rejectionHistory.map((entry, index) => (
                            <tr key={index}>
                                <td>{index + 1}</td>
                                <td>{entry.rejectedBy}</td>
                                <td>{entry.reason}</td>
                                <td>{entry.dateOfRejection}</td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </Container>

            <Container className="report-container mb-3">
                <h3>Uploaded Documents</h3>
                <ul>
                    <li>PDF 1: {formData.documents.pdf1 || "Not uploaded"}</li>
                    <li>PDF 2: {formData.documents.pdf2 || "Not uploaded"}</li>
                    <li>
                        Other Documents:
                        <ul>
                            {formData.documents.otherDocuments.map((doc, index) => (
                                <li key={index}>{doc}</li>
                            ))}
                        </ul>
                    </li>
                </ul>
            </Container>

            <Container className="mb-5">
                <Row>
                    <Col className="d-flex justify-content-between">
                        <Button variant="secondary" onClick={() => nextStep(true)}>Back</Button>
                        <Button variant="primary" onClick={handleConfirm}>Confirm</Button>
                    </Col>
                </Row>
            </Container>

            {/* Modal for Missing Fields */}
            <Modal show={showMissingModal} onHide={() => setShowMissingModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Missing Fields</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>The following required fields are missing:</p>
                    <ul>
                        {missingFields.map((field, index) => (
                            <li key={index}>{field}</li>
                        ))}
                    </ul>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowMissingModal(false)}>Close</Button>
                </Modal.Footer>
            </Modal>

            {/* Confirmation Modal */}
            <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Confirmation</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>
                        The report will undergo PDTC Processing and Issuer’s Review. Please ensure all details are correct before proceeding.
                    </p> 
                    <strong>
                        Do you wish to continue?
                    </strong>
                </Modal.Body>
                <Modal.Footer className="p-0">
                        <Button 
                            variant="secondary" 
                            onClick={() => setShowConfirmModal(false)}
                        >
                            Go Back
                        </Button>
                        <Button 
                            variant="primary" 
                            onClick={handleContinue} 
                            disabled={loading}
                        >
                            {loading ? <Spinner animation="border" size="sm" /> : "Continue"}
                        </Button>
                </Modal.Footer>
            </Modal>

            {/* Success Modal */}
            <Modal show={showSuccessModal} onHide={handleContinue}>
                <Modal.Header closeButton>
                    <Modal.Title>Success</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>Report Successfully Sent</p>
                </Modal.Body>
            </Modal>
        </>
    );
};

export default ReportReview;
